import { resolveNodeForBreakpoint } from "../../contract/breakpoints.js";
import { getSurfaceCard } from "../../contract/document.js";
import { listInterfacePhases } from "../../contract/widgets.js";
import { parseWidgetTreeKey, widgetVisualTreeKey } from "../../selection/keys.js";
import {
  collectWidgetContract,
  resolveWidgetContractTargetKey,
  type WidgetContractRow,
} from "./widget-contract.js";
import { syncFieldSlotHighlight } from "./slot-highlight.js";
import {
  buildTokenContext,
  indexVariables,
  interpolate,
  isColorValue,
  resolveColor,
} from "../../contract/tokens.js";

type TokenEntry = {
  name: string;
  group: string;
  preview: string;
  isColor: boolean;
};

let activePickerDismiss: (() => void) | null = null;

export interface PropertiesInspectorContext {
  doc: Record<string, unknown> | null;
  selectedKey: string | null;
  resolveNode: (key: string | null) => Record<string, unknown> | null;
  breakpoint: string | null | undefined;
  theme: string;
  surface: string;
  onCommit: () => void;
  widgetScreen?: (widgetName: string) => string | null;
  onWidgetScreen?: (widgetName: string, screenId: string) => void;
  /** Active phase for the selected node. `items` is set on the phase container. */
  phase?: { id: string; title?: string; items: { key: string; label: string }[] | null } | null;
  /** Active widget preview phase (for contract event targets). */
  widgetPreviewPhase?: string | null;
  onSelectKey?: (key: string) => void;
  /** Preview outline while hovering contract rows; pass null to restore selection highlight. */
  onHoverKey?: (key: string | null) => void;
  /** State applied to the first row of the active dataset. Empty string is the base state. */
  datasetState?: string;
  onDatasetState?: (state: string) => void;
}

type SduiNode = Record<string, unknown>;

const ALIGN_OPTS = ["", "start", "center", "end", "space-between", "space-around", "space-evenly"];
const VERTICAL_ALIGN_OPTS = ["", "start", "center", "end", "top", "bottom"];
const VISIBILITY_OPTS = ["visible", "gone"] as const;
const FONT_WEIGHT_OPTS = ["", "regular", "medium", "semibold", "bold"] as const;

let mountEl: HTMLElement | null = null;
let lastInspectKey: string | null = null;
let lastBreakpoint: string | null | undefined;
let layoutAspectLocked = false;

const CONTRACT_SECTION_COLLAPSED_KEY = "bff-viewer-contract-collapsed";

function readContractSectionCollapsed(): boolean {
  try {
    return localStorage.getItem(CONTRACT_SECTION_COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

function writeContractSectionCollapsed(collapsed: boolean): void {
  try {
    if (collapsed) localStorage.setItem(CONTRACT_SECTION_COLLAPSED_KEY, "1");
    else localStorage.removeItem(CONTRACT_SECTION_COLLAPSED_KEY);
  } catch {
    /* private mode */
  }
}

export function resolvePropertiesInspectKey(
  selectedKey: string | null,
  resolveNode: PropertiesInspectorContext["resolveNode"],
): string | null {
  return resolvePropertiesTarget(selectedKey, resolveNode).inspectKey;
}

function resolvePropertiesTarget(
  selectedKey: string | null,
  resolveNode: PropertiesInspectorContext["resolveNode"],
): { inspectKey: string | null; node: SduiNode | null; widgetRoot: string | null } {
  if (!selectedKey) return { inspectKey: null, node: null, widgetRoot: null };
  const parsed = parseWidgetTreeKey(selectedKey);
  if (parsed?.name && !parsed.section) {
    const visualKey = widgetVisualTreeKey(parsed.name);
    const visual = resolveNode(visualKey);
    if (visual && isInspectableNode(visual as SduiNode)) {
      return { inspectKey: visualKey, node: visual as SduiNode, widgetRoot: parsed.name };
    }
  }
  const node = resolveNode(selectedKey);
  return {
    inspectKey: selectedKey,
    node: node as SduiNode | null,
    widgetRoot: null,
  };
}

export function initPropertiesInspector(root: HTMLElement | null): void {
  mountEl = root;
}

export function updatePropertiesInspector(ctx: PropertiesInspectorContext): void {
  if (!mountEl) return;
  const active = document.activeElement;
  const typingInside = mountEl.contains(active)
    && (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement);
  const target = resolvePropertiesTarget(ctx.selectedKey, ctx.resolveNode);
  if (
    typingInside
    && target.inspectKey === lastInspectKey
    && ctx.breakpoint === lastBreakpoint
  ) {
    return;
  }
  if (target.inspectKey !== lastInspectKey) layoutAspectLocked = false;
  lastInspectKey = target.inspectKey;
  lastBreakpoint = ctx.breakpoint;
  mountEl.replaceChildren();
  if (!ctx.doc || !ctx.selectedKey) {
    mountEl.append(emptyState("Выберите узел в Structure или на превью."));
    return;
  }
  const parsedWidget = parseWidgetTreeKey(ctx.selectedKey);
  const widgetContractName = parsedWidget?.name && !parsedWidget.section ? parsedWidget.name : null;
  const rawNode = target.node;
  if (!rawNode || !isInspectableNode(rawNode)) {
    mountEl.append(emptyState("Выберите узел с layout или заливкой."));
    syncFieldSlotHighlight([]);
    return;
  }
  const nodeLabel = document.createElement("p");
  nodeLabel.className = "props-inspector__layer";
  const nodeId = typeof rawNode.id === "string" ? rawNode.id : "";
  const nodeType = String(rawNode.type ?? "узел");
  nodeLabel.textContent = nodeId ? `${nodeType} · ${nodeId}` : nodeType;
  mountEl.append(nodeLabel);
  if (ctx.phase?.id) {
    mountEl.append(buildPhaseSection(
      ctx.phase.title ?? ctx.phase.id,
      ctx.phase.items,
      ctx.onSelectKey,
    ));
  }
  const bp = ctx.breakpoint || null;
  const appearance = readAppearance(rawNode);
  const layout = mergedLayout(rawNode, bp);
  const effective = {
    ...(resolveNodeForBreakpoint(rawNode, bp).node as SduiNode),
    text: appearance.text,
    color: appearance.color,
    font: appearance.font,
    visibility: appearance.visibility,
    direction: layout.direction,
    background: appearance.background,
    layout,
  };
  const tokenCtx = buildTokenContext(ctx.doc.tokens as Parameters<typeof buildTokenContext>[0], ctx.theme);
  const tokenEntries = collectTokenEntries(ctx.doc, ctx.theme, ctx.surface, tokenCtx);

  if (layoutBreakpointPatch(rawNode, bp) || whenPatch(rawNode, bp)) {
    const layer = document.createElement("p");
    layer.className = "props-inspector__layer";
    layer.textContent = `Слой breakpoint: ${bp}`;
    mountEl.append(layer);
  }

  const isTextNode = effective.type === "text" || rawNode.type === "text";
  if (isTextNode) {
    mountEl.append(
      buildTextSection(rawNode, effective, bp, tokenEntries, tokenCtx, ctx.onCommit),
      buildVisibilitySection(rawNode, effective, bp, ctx.onCommit),
    );
  } else {
    const fontSection = buildFontSection(rawNode, effective, bp, tokenEntries, tokenCtx, ctx.onCommit);
    mountEl.append(
      buildLayoutSection(rawNode, effective, bp, tokenEntries, tokenCtx, ctx.onCommit),
      ...(fontSection ? [fontSection] : []),
      buildVisibilitySection(rawNode, effective, bp, ctx.onCommit),
      buildFillSection(rawNode, effective, bp, tokenEntries, tokenCtx, ctx.onCommit),
    );
    const states = buildStatesSection(rawNode, ctx.datasetState ?? "", ctx.onDatasetState);
    if (states) mountEl.append(states);
  }
  if (widgetContractName) {
    mountEl.append(buildWidgetContractSection(ctx, widgetContractName));
  }
  syncFieldSlotHighlight([]);
}

function widgetSpec(doc: Record<string, unknown>, name: string): Record<string, unknown> | null {
  const root = doc.widgets && typeof doc.widgets === "object"
    ? doc.widgets as Record<string, unknown>
    : null;
  const chrome = doc.chrome && typeof doc.chrome === "object"
    ? (doc.chrome as Record<string, unknown>).widgets
    : null;
  const widgets = root ?? (chrome && typeof chrome === "object" ? chrome as Record<string, unknown> : null);
  const spec = widgets?.[name];
  return spec && typeof spec === "object" ? spec as Record<string, unknown> : null;
}

function buildWidgetContractSection(ctx: PropertiesInspectorContext, widgetName: string): HTMLElement {
  const wrap = document.createElement("section");
  wrap.className = "props-section props-contract";
  const collapsed = readContractSectionCollapsed();
  if (collapsed) wrap.classList.add("is-collapsed");

  const head = document.createElement("div");
  head.className = "props-contract__section-head";
  const toggle = document.createElement("button");
  toggle.type = "button";
  toggle.className = "props-section__collapse";
  toggle.setAttribute("aria-expanded", String(!collapsed));
  toggle.setAttribute("aria-controls", "props-contract-body");
  toggle.title = collapsed ? "Развернуть контракт" : "Свернуть контракт";
  const chevron = document.createElement("span");
  chevron.className = "props-section__chevron";
  chevron.setAttribute("aria-hidden", "true");
  chevron.innerHTML = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M4 2.5 8 6 4 9.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  const title = document.createElement("span");
  title.className = "props-section__title props-section__title--disclosure";
  title.textContent = "Контракт";
  toggle.append(chevron, title);
  toggle.addEventListener("click", () => {
    const collapsed = wrap.classList.toggle("is-collapsed");
    toggle.setAttribute("aria-expanded", String(!collapsed));
    toggle.title = collapsed ? "Развернуть контракт" : "Свернуть контракт";
    writeContractSectionCollapsed(collapsed);
  });
  head.append(toggle);
  wrap.append(head);

  const body = document.createElement("div");
  body.className = "props-contract__body";
  body.id = "props-contract-body";

  const intro = document.createElement("p");
  intro.className = "props-contract__intro";
  intro.textContent =
    `${widgetName}: поля #{name}, id контейнеров, внутренние события и каталог глобальных действий.`;
  body.append(intro);

  const spec = widgetSpec(ctx.doc!, widgetName);
  const phaseId = ctx.widgetPreviewPhase ?? null;
  const { fields, ids, widgetEvents, globalEvents, otherGlobalEvents } = collectWidgetContract(spec);
  const interfacePhases = listInterfacePhases(spec);

  const contractRowLabel = (row: WidgetContractRow): string => {
    if (row.kind === "field") return `#{${row.name}}`;
    if (row.kind === "id") return row.id;
    return row.name;
  };

  const buildGroup = (
    heading: string,
    rows: WidgetContractRow[],
    emptyLabel: string,
  ) => {
    const elements = rows.flatMap((row) => {
      const targetKey = resolveWidgetContractTargetKey(spec, widgetName, phaseId, row);
      const canSelect = Boolean(targetKey && ctx.resolveNode(targetKey) && ctx.onSelectKey);
      const wired = row.kind === "id" ? row.used : canSelect;
      return [
        contractRow(
          contractRowLabel(row),
          row.title,
          wired,
          canSelect ? targetKey! : null,
          ctx.onSelectKey,
          ctx.onHoverKey,
          ctx.selectedKey,
        ),
      ];
    });
    return contractGroup(heading, elements.length ? elements : [contractEmpty(emptyLabel)]);
  };

  const phaseElements = interfacePhases.map((row) => contractRow(
    row.id,
    row.title,
    false,
    null,
    ctx.onSelectKey,
    ctx.onHoverKey,
    ctx.selectedKey,
  ));

  body.append(
    buildGroup("Поля", fields, "В interface.fields нет полей."),
    buildGroup("Идентификаторы", ids, "В interface.ids нет контейнеров."),
    buildGroup("События виджета", widgetEvents, "В interface.events нет внутренних событий."),
    contractGroup(
      "Фазы",
      phaseElements.length ? phaseElements : [contractEmpty("В interface.phases нет фаз.")],
    ),
    buildGroup("Глобальные события", globalEvents, "На узлах нет глобальных событий из каталога."),
    buildGroup("Не подключены", otherGlobalEvents, "Все глобальные события подключены к виджету."),
  );
  wrap.append(body);
  return wrap;
}

function contractGroup(label: string, rows: HTMLElement[], extra: HTMLElement | null = null): HTMLElement {
  const group = document.createElement("div");
  group.className = "props-contract__group";
  const head = document.createElement("div");
  head.className = "props-contract__head";
  const heading = document.createElement("div");
  heading.className = "props-contract__label";
  heading.textContent = label;
  head.append(heading);
  if (extra) head.append(extra);
  const list = document.createElement("div");
  list.className = "props-contract__list";
  list.append(...rows);
  group.append(head, list);
  return group;
}

function contractRow(
  name: string,
  meta: string,
  wired: boolean,
  targetKey: string | null,
  onSelect: PropertiesInspectorContext["onSelectKey"],
  onHover: PropertiesInspectorContext["onHoverKey"],
  selectedKey: string | null | undefined,
): HTMLElement {
  const row = document.createElement(targetKey ? "button" : "div");
  row.className = `props-contract__row${wired ? " props-contract__row--wired" : " props-contract__row--unwired"}`;
  if (targetKey) {
    row.classList.add("props-contract__row--link");
    if (targetKey === selectedKey) row.classList.add("is-selected");
    (row as HTMLButtonElement).type = "button";
    row.title = "Показать узел на превью";
    row.addEventListener("click", () => onSelect?.(targetKey));
    row.addEventListener("mouseenter", () => onHover?.(targetKey));
    row.addEventListener("mouseleave", () => onHover?.(null));
  }
  const status = document.createElement("span");
  status.className = "props-contract__status";
  status.setAttribute("aria-hidden", "true");
  status.title = wired ? "Подключено к дереву" : "Не используется на дереве";
  const copy = document.createElement("div");
  copy.className = "props-contract__copy";
  const nameEl = document.createElement("code");
  nameEl.className = "props-contract__name";
  nameEl.textContent = name;
  const metaEl = document.createElement("span");
  metaEl.className = "props-contract__meta";
  metaEl.textContent = meta;
  copy.append(nameEl, metaEl);
  row.append(status, copy);
  return row;
}

function contractEmpty(message: string): HTMLElement {
  const p = document.createElement("p");
  p.className = "props-contract__empty";
  p.textContent = message;
  return p;
}

function buildPhaseSection(
  phaseTitle: string,
  rows: { key: string; label: string }[] | null,
  onSelect?: (key: string) => void,
): HTMLElement {
  const wrap = document.createElement("section");
  wrap.className = "props-section props-phase";
  const title = document.createElement("h3");
  title.className = "props-section__title";
  title.textContent = phaseTitle;
  wrap.append(title);
  if (!rows) return wrap;
  const note = document.createElement("p");
  note.className = "props-contract__intro";
  note.textContent = "Элементы этой фазы стоят в контейнере.";
  wrap.append(note);
  for (const row of rows) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "props-phase__item";
    button.textContent = row.label;
    button.addEventListener("click", () => onSelect?.(row.key));
    wrap.append(button);
  }
  return wrap;
}

function emptyState(message: string): HTMLElement {
  const p = document.createElement("p");
  p.className = "props-inspector__empty";
  p.textContent = message;
  return p;
}

function isInspectableNode(node: SduiNode): boolean {
  if (!node || typeof node !== "object") return false;
  const type = node.type;
  if (type === "container" || type === "text" || type === "image" || type === "icon") return true;
  return Boolean(node.direction || node.layout || node.background || node.font);
}

type WhenScope = "direction" | "layout" | "visibility" | "background" | "font" | "color" | "text";

function whenPatch(node: SduiNode, breakpoint: string | null): SduiNode | undefined {
  if (!breakpoint) return undefined;
  const when = node.when as SduiNode | undefined;
  const patch = when?.[breakpoint];
  return patch && typeof patch === "object" ? (patch as SduiNode) : undefined;
}

function readAppearance(node: SduiNode): SduiNode {
  const appearance = node.appearance;
  return appearance && typeof appearance === "object" ? appearance as SduiNode : {};
}

function ensureAppearance(node: SduiNode): SduiNode {
  if (!node.appearance || typeof node.appearance !== "object") node.appearance = {};
  return node.appearance as SduiNode;
}

function layoutBreakpointPatch(node: SduiNode, breakpoint: string | null): SduiNode | undefined {
  if (!breakpoint) return undefined;
  const layout = node.layout as SduiNode | undefined;
  const breakpoints = layout?.breakpoints as SduiNode | undefined;
  const patch = breakpoints?.[breakpoint];
  return patch && typeof patch === "object" ? patch as SduiNode : undefined;
}

function backgroundSolidColor(background: unknown): string | undefined {
  if (!background || typeof background !== "object" || Array.isArray(background)) return undefined;
  const bg = background as SduiNode;
  if (bg.type && bg.type !== "solid") return undefined;
  const colors = bg.colors;
  if (Array.isArray(colors) && colors.length) return String(colors[0]);
  if (typeof bg.color === "string") return bg.color;
  return undefined;
}

/** Patch layer only when that scope is already overridden in when[bp] (see plan). */
function getWriteRoot(node: SduiNode, breakpoint: string | null, scope: WhenScope): SduiNode {
  const patch = whenPatch(node, breakpoint);
  if (patch && patch[scope] !== undefined) return patch;
  return node;
}

function mergedLayout(rawNode: SduiNode, breakpoint: string | null): SduiNode {
  const base = (rawNode.layout || {}) as SduiNode;
  const patchLayout = (layoutBreakpointPatch(rawNode, breakpoint)
    || (whenPatch(rawNode, breakpoint)?.layout || {})) as SduiNode;
  const merged = { ...base, ...patchLayout };
  delete merged.breakpoints;
  if (base.paddings || patchLayout.paddings) {
    merged.paddings = {
      ...(base.paddings as SduiNode),
      ...(patchLayout.paddings as SduiNode),
    };
  }
  if (base.margins || patchLayout.margins) {
    merged.margins = {
      ...(base.margins as SduiNode),
      ...(patchLayout.margins as SduiNode),
    };
  }
  if (base.direction || patchLayout.direction) {
    merged.direction = {
      ...(base.direction as SduiNode),
      ...(patchLayout.direction as SduiNode),
    };
  }
  return merged;
}

function ensureLayoutBucket(node: SduiNode, breakpoint: string | null): SduiNode {
  const patch = layoutBreakpointPatch(node, breakpoint);
  if (patch) return patch;
  if (!node.layout || typeof node.layout !== "object") node.layout = {};
  return node.layout as SduiNode;
}

function mergedFont(rawNode: SduiNode, _breakpoint: string | null): SduiNode {
  const appearance = readAppearance(rawNode);
  const base = (appearance.font || rawNode.font || {}) as SduiNode;
  return { ...base };
}

function ensureFontBucket(node: SduiNode, _breakpoint: string | null): SduiNode {
  const appearance = ensureAppearance(node);
  if (!appearance.font || typeof appearance.font !== "object") appearance.font = {};
  return appearance.font as SduiNode;
}

function applyFontField(
  node: SduiNode,
  breakpoint: string | null,
  field: "size" | "weight",
  next: number | string | null,
): void {
  const bucket = ensureFontBucket(node, breakpoint);
  if (field === "size") {
    if (next === "" || next == null) delete bucket.size;
    else bucket.size = next;
    return;
  }
  if (next) bucket.weight = next;
  else delete bucket.weight;
}

function collectTokenEntries(
  doc: Record<string, unknown>,
  theme: string,
  surface: string,
  tokenCtx: Record<string, unknown>,
): TokenEntry[] {
  const tokens = doc.tokens as SduiNode | undefined;
  const semantic = (tokens?.semantic as SduiNode | undefined)?.[theme] as Record<string, unknown> | undefined;
  const variables = tokens?.variables as Record<string, unknown> | undefined;
  const card = getSurfaceCard(doc, surface);
  const cardVars = indexVariables(card?.variables as Parameters<typeof indexVariables>[0]);

  const out: TokenEntry[] = [];
  const seen = new Set<string>();

  const pushGroup = (group: string, map: Record<string, unknown> | undefined) => {
    if (!map) return;
    for (const name of Object.keys(map).sort()) {
      if (seen.has(name)) continue;
      seen.add(name);
      const raw = String(interpolate(`@{${name}}`, tokenCtx as Record<string, string>));
      const resolvedColor = resolveColor(`@{${name}}`, tokenCtx as Record<string, string>);
      out.push({
        name,
        group,
        preview: raw,
        isColor: isColorValue(raw)
          || isColorValue(String(map[name]))
          || (typeof resolvedColor === "string" && isColorValue(resolvedColor)),
      });
    }
  };

  pushGroup("Semantic", semantic);
  pushGroup("Variables", variables);
  pushGroup("Card", cardVars as Record<string, unknown>);
  return out;
}

function isTokenRef(value: unknown): value is string {
  return typeof value === "string" && /^@\{[^}]+\}$/.test(value);
}

function tokenNameFromRef(ref: string): string {
  return ref.slice(2, -1);
}

function colorTokens(entries: TokenEntry[]): TokenEntry[] {
  return entries.filter((entry) => entry.isColor);
}

function spacingTokens(entries: TokenEntry[]): TokenEntry[] {
  return entries.filter((entry) => !entry.isColor);
}

/** Gap and padding pick spacing tokens, not font or color variables. */
function gapTokens(entries: TokenEntry[]): TokenEntry[] {
  const named = entries.filter((entry) =>
    !entry.isColor && /gap|padding|space/i.test(entry.name) && !/font/i.test(entry.name));
  return named.length
    ? named
    : entries.filter((entry) => !entry.isColor && !/font/i.test(entry.name));
}

function fontSizeTokens(entries: TokenEntry[]): TokenEntry[] {
  return entries.filter(
    (entry) => !entry.isColor && /font-size/i.test(entry.name) && !/font-family/i.test(entry.name),
  );
}

function lineHeightTokens(entries: TokenEntry[]): TokenEntry[] {
  return entries.filter(
    (entry) => !entry.isColor && /font-line|line-height/i.test(entry.name),
  );
}

function tokenColorCss(name: string, tokenCtx: Record<string, unknown>): string | null {
  const resolved = resolveColor(`@{${name}}`, tokenCtx as Record<string, string>);
  return typeof resolved === "string" && resolved ? resolved : null;
}

function literalColorCss(value: string, tokenCtx: Record<string, unknown>): string | null {
  const resolved = resolveColor(value, tokenCtx as Record<string, string>);
  return typeof resolved === "string" && resolved ? resolved : null;
}

function createColorChit(cssColor: string | null | undefined): HTMLSpanElement {
  const chit = document.createElement("span");
  chit.className = "props-color-chit";
  chit.setAttribute("aria-hidden", "true");
  const fill = document.createElement("span");
  fill.className = "props-color-chit__fill";
  if (cssColor) fill.style.backgroundColor = cssColor;
  chit.append(fill);
  return chit;
}

function dismissActivePicker(): void {
  activePickerDismiss?.();
  activePickerDismiss = null;
}

function bindPickerDismiss(root: HTMLElement, menu: HTMLElement): void {
  dismissActivePicker();
  const close = () => {
    menu.hidden = true;
    document.removeEventListener("mousedown", onDocMouseDown);
    if (activePickerDismiss === close) activePickerDismiss = null;
  };
  const onDocMouseDown = (event: MouseEvent) => {
    if (!root.contains(event.target as Node)) close();
  };
  document.addEventListener("mousedown", onDocMouseDown);
  activePickerDismiss = close;
}

function defaultSpacingToken(entries: TokenEntry[]): string {
  const preferred = ["space-2", "space-3", "space-4", "gap-2"];
  for (const name of preferred) {
    if (entries.some((entry) => entry.name === name)) return name;
  }
  return entries[0]?.name ?? "space-2";
}

function section(title: string, children: HTMLElement[]): HTMLElement {
  const wrap = document.createElement("section");
  wrap.className = "props-section";
  const h = document.createElement("h3");
  h.className = "props-section__title";
  h.textContent = title;
  wrap.append(h);
  for (const child of children) wrap.append(child);
  return wrap;
}

function field(label: string, control: HTMLElement): HTMLElement {
  const row = document.createElement("div");
  row.className = "props-field";
  const lab = document.createElement("span");
  lab.className = "props-field__label";
  lab.textContent = label;
  const box = document.createElement("div");
  box.className = "props-field__control";
  box.append(control);
  row.append(lab, box);
  return row;
}

function buildFontFieldRows(
  rawNode: SduiNode,
  font: SduiNode,
  breakpoint: string | null,
  tokenEntries: ReturnType<typeof collectTokenEntries>,
  tokenCtx: Record<string, unknown>,
  onCommit: () => void,
): HTMLElement[] {
  return [
    scalarTokenField(
      "Size",
      font.size,
      fontSizeTokens(tokenEntries),
      tokenCtx,
      (next) => {
        applyFontField(rawNode, breakpoint, "size", next);
        onCommit();
      },
    ),
    selectField(
      "Weight",
      FONT_WEIGHT_OPTS,
      String(font.weight ?? ""),
      (value) => {
        applyFontField(rawNode, breakpoint, "weight", value || null);
        onCommit();
      },
    ),
    scalarTokenField(
      "Line height",
      font.line_height,
      lineHeightTokens(tokenEntries),
      tokenCtx,
      (next) => {
        const bucket = ensureFontBucket(rawNode, breakpoint);
        if (next === "" || next == null) delete bucket.line_height;
        else bucket.line_height = next;
        onCommit();
      },
    ),
  ];
}

function buildFontSection(
  rawNode: SduiNode,
  effective: SduiNode,
  breakpoint: string | null,
  tokenEntries: ReturnType<typeof collectTokenEntries>,
  tokenCtx: Record<string, unknown>,
  onCommit: () => void,
): HTMLElement | null {
  const font = mergedFont(rawNode, breakpoint);
  const contentColor = readAppearance(rawNode).color ?? effective.color ?? rawNode.color;
  const hasOwnFont = Object.keys(font).length > 0;
  if (!hasOwnFont && contentColor === undefined) return null;

  const [sizeRow, weightRow, lineHeightRow] = buildFontFieldRows(
    rawNode,
    font,
    breakpoint,
    tokenEntries,
    tokenCtx,
    onCommit,
  );
  const rows: HTMLElement[] = [];
  if (font.size !== undefined) rows.push(sizeRow);
  if (font.weight !== undefined) rows.push(weightRow);
  if (font.line_height !== undefined) rows.push(lineHeightRow);
  if (contentColor !== undefined) {
    rows.push(
      colorTokenField(
        "Text color",
        contentColor,
        tokenEntries,
        tokenCtx,
        (next) => {
          const appearance = ensureAppearance(rawNode);
          if (next) appearance.color = next;
          else delete appearance.color;
          onCommit();
        },
      ),
    );
  }
  return section("Font", rows);
}

function buildTextSection(
  rawNode: SduiNode,
  effective: SduiNode,
  breakpoint: string | null,
  tokenEntries: ReturnType<typeof collectTokenEntries>,
  tokenCtx: Record<string, unknown>,
  onCommit: () => void,
): HTMLElement {
  const font = mergedFont(rawNode, breakpoint);
  const appearance = readAppearance(rawNode);

  const textInput = document.createElement("textarea");
  textInput.className = "props-text-input";
  textInput.rows = 3;
  textInput.value = String(appearance.text ?? effective.text ?? "");
  textInput.addEventListener("change", () => {
    const target = ensureAppearance(rawNode);
    const next = textInput.value;
    if (next) target.text = next;
    else delete target.text;
    onCommit();
  });

  const colorField = colorTokenField(
    "Color",
    appearance.color ?? effective.color,
    tokenEntries,
    tokenCtx,
    (next) => {
      const target = ensureAppearance(rawNode);
      if (next) target.color = next;
      else delete target.color;
      onCommit();
    },
  );

  return section("Text", [
    field("Content", textInput),
    colorField,
    ...buildFontFieldRows(rawNode, font, breakpoint, tokenEntries, tokenCtx, onCommit),
  ]);
}

const LAYOUT_FLOWS: { id: string; label: string; markup: string }[] = [
  {
    id: "",
    label: "Freeform",
    markup: `<rect x="3" y="4" width="7" height="5" rx="1" fill="currentColor"/><rect x="14" y="3" width="7" height="5" rx="1" fill="currentColor"/><rect x="8" y="14" width="7" height="5" rx="1" fill="currentColor"/>`,
  },
  {
    id: "vertical",
    label: "Vertical",
    markup: `<rect x="3" y="4" width="9" height="5" rx="1" fill="currentColor"/><rect x="3" y="11" width="9" height="5" rx="1" fill="currentColor"/><path d="M17 6.5v8.2M14.8 12.4 17 14.8l2.2-2.4" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>`,
  },
  {
    id: "horizontal",
    label: "Horizontal",
    markup: `<rect x="3" y="4" width="6" height="6" rx="1" fill="currentColor"/><rect x="11" y="4" width="6" height="6" rx="1" fill="currentColor"/><path d="M5 16.5h10.2M12.8 14.3 15.2 16.5l-2.4 2.2" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>`,
  },
  {
    id: "grid",
    label: "Grid",
    markup: `<rect x="4" y="4" width="6" height="6" rx="1" fill="currentColor"/><rect x="13" y="4" width="6" height="6" rx="1" fill="currentColor"/><rect x="4" y="13" width="6" height="6" rx="1" fill="currentColor"/><rect x="13" y="13" width="6" height="6" rx="1" fill="currentColor"/>`,
  },
];

const FIT_ICON = `<path fill="currentColor" d="M7.2 6.4 9.6 4H6.8v2.8L7.2 6.4Zm9.6 0L14.4 4H17.2v2.8l-.4-.4ZM7.2 17.6 9.6 20H6.8v-2.8l.4.4Zm9.6 0L14.4 20h2.8v-2.8l-.4.4Z"/>`;
const AUTO_ICON = `<path fill="currentColor" fill-rule="evenodd" d="M8 6.5h2.2a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1Zm5.2 0H16a1 1 0 0 1 1 1v3.2a1 1 0 0 1-1 1h-2.8a1 1 0 0 1-1-1V7.5a1 1 0 0 1 1-1ZM15 14.2v1.6h-1.6v1.2H15V18.6h1.2v-1.6h1.6V15.8H16.2v-1.6H15Z" clip-rule="evenodd"/>`;
const LOCK_ICON = `<path fill="currentColor" fill-rule="evenodd" d="M8 8.2h8v8H8v-8Zm-1.2 0A1.2 1.2 0 0 1 8 7h8a1.2 1.2 0 0 1 1.2 1.2v8A1.2 1.2 0 0 1 16 17.4H8a1.2 1.2 0 0 1-1.2-1.2v-8Zm3 2.2h2.2v1.4H9.8V10.4Zm2.6 3.2H11v1.4h1.4V13.6Z" clip-rule="evenodd"/>`;

function layoutSvg(markup: string): SVGSVGElement {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("width", "16");
  svg.setAttribute("height", "16");
  svg.setAttribute("aria-hidden", "true");
  svg.innerHTML = markup;
  return svg;
}

function layoutIconButton(title: string, markup: string, pressed: boolean, onClick: () => void): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = `props-layout__icon${pressed ? " is-on" : ""}`;
  button.title = title;
  button.setAttribute("aria-label", title);
  button.setAttribute("aria-pressed", String(pressed));
  button.append(layoutSvg(markup));
  button.addEventListener("click", onClick);
  return button;
}

function fixedSizeNumber(spec: SduiNode | undefined): number | null {
  if (spec?.type !== "fixed" || typeof spec.value !== "number" || !Number.isFinite(spec.value) || spec.value === 0) {
    return null;
  }
  return spec.value;
}

function dimensionLabel(spec: SduiNode | undefined): string {
  const type = String(spec?.type || "wrap_content");
  if (type === "match_parent") return "Fill";
  if (type === "wrap_content") return "Hug";
  return formatScalar(spec?.value);
}

function applyDimensionText(
  rawNode: SduiNode,
  breakpoint: string | null,
  axis: "width" | "height",
  text: string,
): void {
  const trimmed = text.trim();
  const lower = trimmed.toLowerCase();
  if (!trimmed || lower === "hug" || lower === "wrap") {
    applySizeSpec(rawNode, breakpoint, axis, "wrap_content", null);
    return;
  }
  if (lower === "fill" || lower === "match") {
    applySizeSpec(rawNode, breakpoint, axis, "match_parent", null);
    return;
  }
  applySizeSpec(rawNode, breakpoint, axis, "fixed", parseScalarInput(trimmed) ?? trimmed);
}

function buildLayoutSection(
  rawNode: SduiNode,
  effective: SduiNode,
  breakpoint: string | null,
  tokenEntries: ReturnType<typeof collectTokenEntries>,
  tokenCtx: Record<string, unknown>,
  onCommit: () => void,
): HTMLElement {
  const layout = mergedLayout(rawNode, breakpoint);
  const direction = (layout.direction || effective.direction || {}) as SduiNode;
  const paddings = (layout.paddings || {}) as SduiNode;
  const directionType = String(direction.type || "");
  const auto = directionType !== "";

  const setDirection = (value: string) => {
    const lay = ensureLayoutBucket(rawNode, breakpoint);
    if (!value) {
      delete lay.direction;
      onCommit();
      return;
    }
    const dir = (lay.direction || {}) as SduiNode;
    dir.type = value;
    if (value === "grid") {
      delete dir.horizontal;
      delete dir.vertical;
    }
    lay.direction = dir;
    onCommit();
  };

  const wrap = document.createElement("section");
  wrap.className = "props-section props-layout";

  const head = document.createElement("div");
  head.className = "props-layout__head";
  const title = document.createElement("h3");
  title.className = "props-section__title";
  title.textContent = "Layout";
  const tools = document.createElement("div");
  tools.className = "props-layout__tools";
  tools.append(
    layoutIconButton("Resize to fit", FIT_ICON, false, () => {
      applySizeSpec(rawNode, breakpoint, "width", "wrap_content", null);
      applySizeSpec(rawNode, breakpoint, "height", "wrap_content", null);
      onCommit();
    }),
    layoutIconButton("Auto layout", AUTO_ICON, auto, () => {
      setDirection(auto ? "" : (directionType || "vertical"));
    }),
  );
  head.append(title, tools);
  wrap.append(head);

  const flowGroup = document.createElement("fieldset");
  flowGroup.className = "props-layout__group";
  const flowLegend = document.createElement("legend");
  flowLegend.className = "props-layout__legend";
  flowLegend.textContent = "Flow";
  const flow = document.createElement("div");
  flow.className = "props-flow";
  flow.setAttribute("role", "radiogroup");
  flow.setAttribute("aria-label", "Flow");
  const flows = directionType === "overlap"
    ? [...LAYOUT_FLOWS, {
      id: "overlap",
      label: "Overlap",
      markup: `<rect x="6" y="5" width="12" height="9" rx="1" fill="currentColor" opacity="0.45"/><rect x="4" y="8" width="12" height="9" rx="1" fill="currentColor"/>`,
    }]
    : LAYOUT_FLOWS;
  for (const item of flows) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = `props-flow__btn${item.id === directionType ? " is-on" : ""}`;
    button.title = item.label;
    button.setAttribute("aria-label", item.label);
    button.setAttribute("aria-pressed", String(item.id === directionType));
    button.append(layoutSvg(item.markup));
    button.addEventListener("click", () => setDirection(item.id));
    flow.append(button);
  }
  flowGroup.append(flowLegend, flow);
  wrap.append(flowGroup);

  const dimGroup = document.createElement("fieldset");
  dimGroup.className = "props-layout__group";
  const dimLegend = document.createElement("legend");
  dimLegend.className = "props-layout__legend";
  dimLegend.textContent = "Dimensions";
  const dims = document.createElement("div");
  dims.className = "props-dims";

  const widthSpec = layout.width as SduiNode | undefined;
  const heightSpec = layout.height as SduiNode | undefined;
  const ratio = (() => {
    const width = fixedSizeNumber(widthSpec);
    const height = fixedSizeNumber(heightSpec);
    return width != null && height != null ? width / height : null;
  })();

  const commitAxis = (axis: "width" | "height", text: string) => {
    applyDimensionText(rawNode, breakpoint, axis, text);
    if (layoutAspectLocked && ratio != null) {
      const parsed = Number(text);
      if (Number.isFinite(parsed) && parsed !== 0) {
        const other = axis === "width" ? parsed / ratio : parsed * ratio;
        applySizeSpec(
          rawNode,
          breakpoint,
          axis === "width" ? "height" : "width",
          "fixed",
          Math.round(other * 100) / 100,
        );
      }
    }
    onCommit();
  };

  dims.append(
    dimensionControl("W", "Width", widthSpec, (text) => commitAxis("width", text)),
    dimensionControl("H", "Height", heightSpec, (text) => commitAxis("height", text)),
    layoutIconButton("Lock aspect ratio", LOCK_ICON, layoutAspectLocked, () => {
      layoutAspectLocked = !layoutAspectLocked;
      onCommit();
    }),
  );
  dimGroup.append(dimLegend, dims);
  wrap.append(dimGroup);

  if (directionType === "horizontal" || directionType === "vertical" || directionType === "overlap") {
    wrap.append(
      selectField("Main axis", ALIGN_OPTS, String(direction.horizontal || ""), (value) => {
        const lay = ensureLayoutBucket(rawNode, breakpoint);
        const dir = (lay.direction || {}) as SduiNode;
        if (value) dir.horizontal = value;
        else delete dir.horizontal;
        lay.direction = dir;
        onCommit();
      }),
      selectField("Cross axis", VERTICAL_ALIGN_OPTS, String(direction.vertical || ""), (value) => {
        const lay = ensureLayoutBucket(rawNode, breakpoint);
        const dir = (lay.direction || {}) as SduiNode;
        if (value) dir.vertical = value;
        else delete dir.vertical;
        lay.direction = dir;
        onCommit();
      }),
    );
  }
  if (directionType === "grid") {
    const columns = document.createElement("input");
    columns.type = "text";
    columns.value = String(layout.grid_columns ?? layout.gridColumns ?? "");
    columns.addEventListener("change", () => {
      const lay = ensureLayoutBucket(rawNode, breakpoint);
      const next = columns.value.trim();
      if (next) lay.grid_columns = next;
      else delete lay.grid_columns;
      onCommit();
    });
    wrap.append(field("Columns", columns));
  }
  wrap.append(scalarTokenField(
    "Gap",
    layout.gap,
    gapTokens(tokenEntries),
    tokenCtx,
    (next) => {
      const lay = ensureLayoutBucket(rawNode, breakpoint);
      if (next === "" || next == null) delete lay.gap;
      else lay.gap = next;
      onCommit();
    },
  ));

  const paddingWrap = document.createElement("div");
  paddingWrap.className = "props-paddings";
  for (const side of ["top", "right", "bottom", "left"] as const) {
    const label = document.createElement("label");
    label.textContent = side;
    const input = document.createElement("input");
    input.type = "text";
    input.inputMode = "decimal";
    input.value = formatScalar(paddings[side] as unknown);
    const commitPadding = () => {
      const lay = ensureLayoutBucket(rawNode, breakpoint);
      const owner = getWriteRoot(rawNode, breakpoint, "layout");
      const basePad = ((rawNode.layout as SduiNode | undefined)?.paddings || {}) as SduiNode;
      const pad = owner === rawNode
        ? { ...(lay.paddings as SduiNode | undefined) }
        : { ...basePad, ...(lay.paddings as SduiNode | undefined) };
      const parsed = parseScalarInput(input.value);
      if (parsed === undefined) delete pad[side];
      else pad[side] = parsed;
      if (Object.keys(pad).length) lay.paddings = pad;
      else delete lay.paddings;
      onCommit();
    };
    input.addEventListener("change", commitPadding);
    input.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        commitPadding();
      }
    });
    label.append(input);
    paddingWrap.append(label);
  }
  wrap.append(field("Padding", paddingWrap));

  const clipLabel = document.createElement("label");
  clipLabel.className = "props-clip";
  const clip = document.createElement("input");
  clip.type = "checkbox";
  clip.checked = layout.clip === true;
  clip.addEventListener("change", () => {
    const lay = ensureLayoutBucket(rawNode, breakpoint);
    if (clip.checked) lay.clip = true;
    else delete lay.clip;
    onCommit();
  });
  clipLabel.append(clip, document.createTextNode("Clip content"));
  wrap.append(clipLabel);
  return wrap;
}

function dimensionControl(
  axisLabel: string,
  name: string,
  spec: SduiNode | undefined,
  onCommitText: (text: string) => void,
): HTMLElement {
  const box = document.createElement("label");
  box.className = "props-dim";
  const axis = document.createElement("span");
  axis.className = "props-dim__axis";
  axis.textContent = axisLabel;
  const input = document.createElement("input");
  input.type = "text";
  input.setAttribute("aria-label", name);
  input.value = dimensionLabel(spec);
  input.addEventListener("change", () => onCommitText(input.value));
  input.addEventListener("keydown", (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      onCommitText(input.value);
    }
  });
  const mode = document.createElement("select");
  mode.setAttribute("aria-label", `${name} mode`);
  const current = String(spec?.type || "wrap_content");
  for (const [value, label] of [["wrap_content", "Hug"], ["match_parent", "Fill"], ["fixed", "Fixed"]] as const) {
    const option = document.createElement("option");
    option.value = value;
    option.textContent = label;
    if (value === current) option.selected = true;
    mode.append(option);
  }
  mode.addEventListener("change", () => {
    if (mode.value === "match_parent") onCommitText("Fill");
    else if (mode.value === "wrap_content") onCommitText("Hug");
    else onCommitText(spec?.type === "fixed" ? formatScalar(spec.value) : "0");
  });
  box.append(axis, input, mode);
  return box;
}

function buildVisibilitySection(
  rawNode: SduiNode,
  effective: SduiNode,
  breakpoint: string | null,
  onCommit: () => void,
): HTMLElement {
  const current = (readAppearance(rawNode).visibility ?? effective.visibility) === "gone" ? "gone" : "visible";
  return section("Visibility", [
    selectField("Mode", VISIBILITY_OPTS, current, (value) => {
      const appearance = ensureAppearance(rawNode);
      if (value === "visible") delete appearance.visibility;
      else appearance.visibility = "gone";
      onCommit();
    }),
  ]);
}

function variantNames(rawNode: SduiNode): string[] {
  const variants = rawNode.variants;
  if (!variants || typeof variants !== "object" || Array.isArray(variants)) return [];
  return Object.keys(variants).filter((name) => {
    const patch = (variants as SduiNode)[name];
    return Boolean(patch) && typeof patch === "object" && !Array.isArray(patch);
  });
}

function buildStatesSection(
  rawNode: SduiNode,
  datasetState: string,
  onDatasetState: ((state: string) => void) | undefined,
): HTMLElement | null {
  const names = variantNames(rawNode);
  if (!names.length) return null;
  const wrap = document.createElement("section");
  wrap.className = "props-section";
  const label = document.createElement("label");
  label.className = "viewport widget-preview-state";
  label.append(document.createTextNode("Состояние"));
  const select = document.createElement("select");
  select.setAttribute("aria-label", "Состояние первого элемента списка");
  const base = document.createElement("option");
  base.value = "";
  base.textContent = "Базовое";
  select.append(base);
  for (const name of names) {
    const option = document.createElement("option");
    option.value = name;
    option.textContent = name;
    select.append(option);
  }
  select.value = names.includes(datasetState) ? datasetState : "";
  select.addEventListener("change", () => {
    onDatasetState?.(select.value);
  });
  label.append(select);
  wrap.append(label);
  return wrap;
}

function buildFillSection(
  rawNode: SduiNode,
  effective: SduiNode,
  breakpoint: string | null,
  tokenEntries: ReturnType<typeof collectTokenEntries>,
  tokenCtx: Record<string, unknown>,
  onCommit: () => void,
): HTMLElement {
  const background = readAppearance(rawNode).background ?? effective.background;
  const solidColor = backgroundSolidColor(background);
  if (solidColor === undefined) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "props-add-fill";
    btn.textContent = "Добавить заливку";
    btn.addEventListener("click", () => {
      ensureAppearance(rawNode).background = { type: "solid", colors: ["@{surface-primary}"] };
      onCommit();
    });
    return section("Fill", [field("", btn)]);
  }

  const colorField = colorTokenField(
    "Color",
    solidColor,
    tokenEntries,
    tokenCtx,
    (next) => {
      const appearance = ensureAppearance(rawNode);
      if (!next) {
        delete appearance.background;
      } else {
        const current = appearance.background;
        const bg = current && typeof current === "object" && !Array.isArray(current)
          ? { ...(current as SduiNode) }
          : { type: "solid" };
        bg.type = "solid";
        bg.colors = [next];
        delete bg.color;
        appearance.background = bg;
      }
      onCommit();
    },
  );
  return section("Fill", [colorField]);
}

function selectField(
  label: string,
  options: readonly string[],
  current: string,
  onChange: (value: string) => void,
): HTMLElement {
  const select = document.createElement("select");
  for (const opt of options) {
    const option = document.createElement("option");
    option.value = opt;
    option.textContent = opt || "—";
    if (opt === current) option.selected = true;
    select.append(option);
  }
  select.addEventListener("change", () => onChange(select.value));
  return field(label, select);
}

function formatScalar(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "number") return String(value);
  if (typeof value === "string") return value;
  return "";
}

function parseScalarInput(text: string): number | string | undefined {
  const trimmed = text.trim();
  if (!trimmed) return undefined;
  if (isTokenRef(trimmed)) return trimmed;
  const num = Number(trimmed);
  if (Number.isFinite(num)) return num;
  return trimmed;
}

function scalarTokenField(
  label: string,
  value: unknown,
  tokenEntries: ReturnType<typeof collectTokenEntries>,
  _tokenCtx: Record<string, unknown>,
  onChange: (value: number | string | null) => void,
): HTMLElement {
  return field(label, buildScalarControls(value, tokenEntries, onChange));
}

function colorTokenField(
  label: string,
  value: unknown,
  tokenEntries: TokenEntry[],
  tokenCtx: Record<string, unknown>,
  onChange: (value: string) => void,
): HTMLElement {
  const entries = colorTokens(tokenEntries);
  const selectedToken = isTokenRef(value) ? tokenNameFromRef(value) : "";
  const isCustom = !selectedToken && typeof value === "string" && value.length > 0;

  const root = document.createElement("div");
  root.className = "props-color-picker";

  const trigger = document.createElement("button");
  trigger.type = "button";
  trigger.className = "props-color-picker__trigger";
  trigger.setAttribute("aria-haspopup", "listbox");
  trigger.setAttribute("aria-expanded", "false");

  const triggerChit = createColorChit(
    selectedToken
      ? tokenColorCss(selectedToken, tokenCtx)
      : (isCustom ? literalColorCss(value as string, tokenCtx) : null),
  );
  const triggerLabel = document.createElement("span");
  triggerLabel.className = "props-color-picker__name";
  triggerLabel.textContent = selectedToken
    ? selectedToken
    : (isCustom ? "Своё значение" : "Выберите цвет");

  const chevron = document.createElement("span");
  chevron.className = "props-color-picker__chevron";
  chevron.setAttribute("aria-hidden", "true");
  chevron.textContent = "▾";

  trigger.append(triggerChit, triggerLabel, chevron);

  const menu = document.createElement("div");
  menu.className = "props-color-picker__menu";
  menu.hidden = true;
  menu.setAttribute("role", "listbox");

  let lastGroup = "";
  for (const entry of entries) {
    if (entry.group !== lastGroup) {
      lastGroup = entry.group;
      const group = document.createElement("div");
      group.className = "props-color-picker__group";
      group.textContent = entry.group;
      menu.append(group);
    }
    const option = document.createElement("button");
    option.type = "button";
    option.className = "props-color-picker__option";
    option.setAttribute("role", "option");
    if (entry.name === selectedToken) option.classList.add("is-selected");
    const textCol = document.createElement("div");
    textCol.className = "props-color-picker__option-text";
    const nameEl = document.createElement("span");
    nameEl.className = "props-color-picker__option-name";
    nameEl.textContent = entry.name;
    const meta = document.createElement("span");
    meta.className = "props-color-picker__option-meta";
    meta.textContent = entry.preview;
    textCol.append(nameEl, meta);
    option.append(createColorChit(tokenColorCss(entry.name, tokenCtx)), textCol);
    option.addEventListener("click", () => {
      onChange(`@{${entry.name}}`);
      menu.hidden = true;
      trigger.setAttribute("aria-expanded", "false");
      dismissActivePicker();
    });
    menu.append(option);
  }

  const customOption = document.createElement("button");
  customOption.type = "button";
  customOption.className = "props-color-picker__option props-color-picker__option--custom";
  customOption.setAttribute("role", "option");
  if (isCustom) customOption.classList.add("is-selected");
  customOption.textContent = "Своё значение…";
  customOption.addEventListener("click", () => {
    menu.hidden = true;
    trigger.setAttribute("aria-expanded", "false");
    dismissActivePicker();
    customInput.hidden = false;
    customInput.focus();
  });
  menu.append(customOption);

  trigger.addEventListener("click", () => {
    const open = menu.hidden;
    dismissActivePicker();
    menu.hidden = !open;
    trigger.setAttribute("aria-expanded", String(!menu.hidden));
    if (!menu.hidden) {
      const rect = trigger.getBoundingClientRect();
      menu.style.position = "fixed";
      menu.style.top = `${rect.bottom + 4}px`;
      menu.style.left = `${rect.left}px`;
      menu.style.width = `${Math.max(rect.width, 240)}px`;
      bindPickerDismiss(root, menu);
    }
  });

  const customInput = document.createElement("input");
  customInput.type = "text";
  customInput.className = "props-color-picker__custom";
  customInput.placeholder = "#rrggbb, oklch(…) или @{token}";
  customInput.hidden = !isCustom;
  if (isCustom) customInput.value = String(value);
  customInput.addEventListener("change", () => {
    onChange(customInput.value.trim());
  });

  root.append(trigger, menu, customInput);
  return field(label, root);
}

function fillSpacingTokenSelect(
  select: HTMLSelectElement,
  tokenEntries: TokenEntry[],
  selectedName: string,
): void {
  select.replaceChildren();
  select.classList.add("props-spacing-token-select");
  let lastGroup = "";
  for (const entry of tokenEntries) {
    if (entry.group !== lastGroup) {
      lastGroup = entry.group;
      const sep = document.createElement("option");
      sep.disabled = true;
      sep.textContent = `— ${entry.group} —`;
      select.append(sep);
    }
    const option = document.createElement("option");
    option.value = entry.name;
    option.textContent = entry.preview && entry.preview !== entry.name
      ? `@{${entry.name}} · ${entry.preview}`
      : `@{${entry.name}}`;
    if (entry.name === selectedName) option.selected = true;
    select.append(option);
  }
  const custom = document.createElement("option");
  custom.value = "__custom__";
  custom.textContent = "Своё значение";
  if (!selectedName) custom.selected = true;
  select.append(custom);
}

function buildScalarControls(
  value: unknown,
  tokenEntries: TokenEntry[],
  onChange: (value: number | string | null) => void,
): HTMLElement {
  const spacing = spacingTokens(tokenEntries);
  const tokenMode = isTokenRef(value);
  const wrap = document.createElement("div");
  wrap.className = "props-field__row";

  const modeBtn = document.createElement("button");
  modeBtn.type = "button";
  modeBtn.className = `props-field__mode${tokenMode ? " is-active" : ""}`;
  modeBtn.textContent = "@";
  modeBtn.title = "Токен отступа / число";

  const input = document.createElement("input");
  input.type = tokenMode ? "text" : "number";
  input.value = tokenMode ? String(value) : value != null && typeof value === "number" ? String(value) : "";

  const tokenSelect = document.createElement("select");
  tokenSelect.hidden = !tokenMode;
  fillSpacingTokenSelect(
    tokenSelect,
    spacing,
    tokenMode && isTokenRef(value) ? tokenNameFromRef(value) : "",
  );

  modeBtn.addEventListener("click", () => {
    const nextToken = !modeBtn.classList.contains("is-active");
    modeBtn.classList.toggle("is-active", nextToken);
    tokenSelect.hidden = !nextToken;
    input.hidden = nextToken;
    if (nextToken) onChange(`@{${defaultSpacingToken(spacing)}}`);
    else onChange(null);
  });

  input.addEventListener("change", () => {
    const parsed = parseScalarInput(input.value);
    onChange(parsed ?? null);
  });

  tokenSelect.addEventListener("change", () => {
    if (tokenSelect.value === "__custom__") {
      input.hidden = false;
      input.type = "text";
      input.focus();
      return;
    }
    onChange(`@{${tokenSelect.value}}`);
  });

  input.hidden = tokenMode;
  wrap.append(modeBtn, tokenSelect, input);
  return wrap;
}

function applySizeSpec(
  rawNode: SduiNode,
  breakpoint: string | null,
  axis: string,
  type: string,
  fixedValue: unknown,
): void {
  const lay = ensureLayoutBucket(rawNode, breakpoint);
  const key = axis === "width" ? "width" : "height";
  if (type === "fixed") {
    const spec: SduiNode = { type: "fixed" };
    if (fixedValue != null && fixedValue !== "") spec.value = fixedValue;
    lay[key] = spec;
  } else {
    lay[key] = { type };
  }
}
